import { useAgentDelegation } from '../hooks';

function AgentDelegation(): JSX.Element {
  const {
    ownerMnemonic,
    setOwnerMnemonic,
    ownerAddress,
    setOwnerAddress,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading,
    generateOwnerAddress,
    generateAgentKeypair,
    prepareAgentSign,
    signAgentApproval,
    isReady
  } = useAgentDelegation();

  return (
    <div className="component">
      <h3>Agent Delegation</h3>
      <p>Approve an agent private key to act on behalf of the owner wallet</p>
      
      <div className="form-group">
        <label htmlFor="ownerMnemonic">Owner Mnemonic (for signing approval):</label>
        <textarea
          id="ownerMnemonic"
          value={ownerMnemonic}
          onChange={(e) => setOwnerMnemonic(e.target.value)}
          placeholder="Enter owner mnemonic phrase..."
          rows={2}
        />
      </div>

      <div className="form-group">
        <label htmlFor="ownerAddress">Owner Address:</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            id="ownerAddress"
            type="text"
            value={ownerAddress}
            onChange={(e) => setOwnerAddress(e.target.value)}
            placeholder="0x..."
            style={{ flex: 1 }}
          />
          <button 
            className="button secondary" 
            onClick={generateOwnerAddress}
            disabled={loading || !isReady}
            style={{ padding: '0.75rem 1rem' }}
          >
            Generate
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="agentAddress">Agent Address:</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            id="agentAddress"
            type="text"
            value={agentAddress}
            onChange={(e) => setAgentAddress(e.target.value)}
            placeholder="0x..."
            style={{ flex: 1 }}
          />
          <button 
            className="button secondary" 
            onClick={generateAgentKeypair}
            disabled={loading || !isReady}
            style={{ padding: '0.75rem 1rem' }}
          >
            Generate Keypair
          </button>
        </div>
      </div>

      {agentPrivateKey && (
        <div className="form-group">
          <label htmlFor="agentPrivateKey">Agent Private Key (save securely):</label>
          <input
            id="agentPrivateKey"
            type="text"
            value={agentPrivateKey}
            readOnly
            style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="agentName">Agent Name (optional):</label>
        <input
          id="agentName"
          type="text"
          value={agentName}
          onChange={(e) => setAgentName(e.target.value)}
          placeholder="TradingBot"
        />
      </div>

      <div className="form-group">
        <label htmlFor="expiresInDays">Expires In Days:</label>
        <input
          id="expiresInDays"
          type="number"
          value={expiresInDays}
          onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 180)}
          min="1"
          max="365"
        />
      </div>

      <div className="form-group">
        <label htmlFor="permissions">Permissions (comma-separated):</label>
        <input
          id="permissions"
          type="text"
          value={permissions}
          onChange={(e) => setPermissions(e.target.value)}
          placeholder="clob::submit_order,clob::cancel_order"
        />
        <small style={{ display: 'block', marginTop: '0.5rem', opacity: 0.8 }}>
          Example: clob::submit_order, clob::cancel_order, clob::modify_order
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="nonce">Nonce:</label>
        <input
          id="nonce"
          type="text"
          value={nonce}
          onChange={(e) => setNonce(e.target.value)}
          placeholder={Date.now().toString()}
        />
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <button 
          className="button secondary" 
          onClick={prepareAgentSign}
          disabled={loading || !isReady || !ownerAddress || !agentAddress}
        >
          {loading ? 'Preparing...' : 'Prepare Agent Sign'}
        </button>
        
        <button 
          className="button" 
          onClick={signAgentApproval}
          disabled={loading || !isReady || !ownerAddress || !agentAddress || !ownerMnemonic}
        >
          {loading ? 'Signing Approval...' : 'Sign Agent Approval'}
        </button>
      </div>

      {result && (
        <div className={`output ${result.success ? 'success' : 'error'}`}>
          {result.success ? (
            <>
              {result.signature && (
                <>
                  <strong>Agent Approval Signature:</strong><br/>
                  {result.signature}<br/><br/>
                </>
              )}
              {result.typedData && (
                <>
                  <strong>EIP-712 Typed Data:</strong><br/>
                  <pre style={{ fontSize: '0.85rem', overflow: 'auto', maxHeight: '300px' }}>
                    {JSON.stringify(result.typedData, null, 2)}
                  </pre>
                  <br/>
                  <strong>Usage:</strong><br/>
                  Use this typed data with MetaMask or other EIP-712 compatible wallets to sign the agent approval.
                </>
              )}
            </>
          ) : (
            <>
              <strong>Error:</strong><br/>
              {result.error}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentDelegation;
